import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import axios from "axios";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import { Spin } from "antd";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

const TABLE_WIDTH = 567;

const LIST_COL = {
  sr: 26,
  name: 130,
  mobile: 72,
  email: 110,
  acType: 72,
  balance: 78,
  type: 79,
};

const SUMMARY_HALF = 283;
const SUMMARY_COL = {
  sr: 22,
  name: 118,
  balance: 78,
  type: 65,
};

const parseBoolParam = (value) =>
  value === true || value === "true" || value === "1";

const parseFilterId = (value) => {
  const id = parseInt(value, 10);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const resolveAsOfDate = (raw) => {
  if (!raw || raw === "null" || raw === "undefined") return null;
  const parsed = moment(raw, ["YYYY-MM-DD", config.date_format], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const fmtMoney = (val) => PrintFormateNumber(parseReportAmount(val), 0);

function PrintAccountList() {
  const urlParams = new URLSearchParams(window.location.search);
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  const orderBy = urlParams.get("OrderBy") || "";
  const balanceTypeFilter = urlParams.get("BalanceType") || "";
  const accountTypeId = parseFilterId(urlParams.get("Account_Type_ID"));
  const hideZeroBalance = parseBoolParam(urlParams.get("HideZeroBalance"));
  const asOfDate = resolveAsOfDate(urlParams.get("Dated"));
  const searchedName = urlParams.get("Name") || "";

  const [reportData, setReportData] = useState({});
  const [listofAccounts, setListofAccounts] = useState([]);
  const [creditAccount, setCreditAccount] = useState([]);
  const [debitAccount, setDebitAccount] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Print Accounts List";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const payload = {
      UserID,
      AccessKey,
      OrderBy: orderBy,
      PageNo: 1,
      PageSize: 20000,
      BalanceType:
        balanceTypeFilter === "Debit" || balanceTypeFilter === "Credit"
          ? balanceTypeFilter
          : "",
      Account_Type_ID: accountTypeId,
      Name: searchedName,
      HideZeroBalance: hideZeroBalance,
    };

    if (asOfDate) {
      payload.Dated = asOfDate;
    }

    axios({
      method: "post",
      url: config.base_url + "account/GetAccountsForPrint",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setListofAccounts(response.data.listofAccounts || []);
          setReportData(response.data);
          setCreditAccount(response.data.listofCreditTypes || []);
          setDebitAccount(response.data.listofDebitTypes || []);
        } else {
          setListofAccounts([]);
          setReportData({});
          setCreditAccount([]);
          setDebitAccount([]);
        }
      })
      .catch(function () {
        setListofAccounts([]);
        setReportData({});
        setCreditAccount([]);
        setDebitAccount([]);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [
    UserID,
    AccessKey,
    orderBy,
    balanceTypeFilter,
    accountTypeId,
    hideZeroBalance,
    asOfDate,
    searchedName,
  ]);

  const subtitleLines = useMemo(() => {
    const lines = [];
    if (asOfDate) {
      lines.push(
        `As of ${moment(asOfDate).format(config.date_format)}`
      );
    } else {
      lines.push("As of today");
    }
    if (accountTypeId > 0) {
      lines.push(`Account type filter applied`);
    }
    if (balanceTypeFilter === "Debit" || balanceTypeFilter === "Credit") {
      lines.push(`Balance type: ${balanceTypeFilter}`);
    }
    if (searchedName) {
      lines.push(`Name contains: ${searchedName}`);
    }
    if (hideZeroBalance) {
      lines.push("Zero balances hidden");
    }
    if (reportData.totalRecords != null) {
      lines.push(`Accounts: ${reportData.totalRecords}`);
    }
    return lines;
  }, [
    asOfDate,
    accountTypeId,
    balanceTypeFilter,
    searchedName,
    hideZeroBalance,
    reportData.totalRecords,
  ]);

  const renderAccountRows = () => {
    if (!listofAccounts.length) return null;

    return listofAccounts.map((item, index) => (
      <View key={item.ID || index} style={saleReportStyles.row}>
        <View style={[saleReportStyles.td, { width: LIST_COL.sr }]}>
          <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: LIST_COL.name }]}>
          <Text style={saleReportStyles.tdValue}>{item.Name || "—"}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: LIST_COL.mobile }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Mobile_No || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: LIST_COL.email }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Email_Address || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: LIST_COL.acType }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Account_Type_BE?.Name || "—"}
          </Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: LIST_COL.balance, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmtMoney(item.Balance)}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: LIST_COL.type }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.BalanceType || "—"}
          </Text>
        </View>
      </View>
    ));
  };

  const renderSummaryRows = (rows) => {
    if (!rows?.length) return null;

    return rows.map((item, index) => (
      <View key={item.ID || index} style={saleReportStyles.row}>
        <View style={[saleReportStyles.td, { width: SUMMARY_COL.sr }]}>
          <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: SUMMARY_COL.name }]}>
          <Text style={saleReportStyles.tdValue}>{item.Name || "—"}</Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: SUMMARY_COL.balance, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmtMoney(item.Balance)}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: SUMMARY_COL.type }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.BalanceType || "—"}
          </Text>
        </View>
      </View>
    ));
  };

  const renderSummaryTable = (title, rows, total, totalType) => (
    <View style={{ width: SUMMARY_HALF }}>
      <Text
        style={[saleReportStyles.pagetitle, { fontSize: "9pt", marginBottom: 4 }]}
      >
        {title}
      </Text>
      <View style={[saleReportStyles.table, { width: SUMMARY_HALF }]}>
        <View style={saleReportStyles.tableHead}>
          <View style={[saleReportStyles.th, { width: SUMMARY_COL.sr }]}>
            <Text style={saleReportStyles.thValue}>Sr.#</Text>
          </View>
          <View style={[saleReportStyles.th, { width: SUMMARY_COL.name }]}>
            <Text style={saleReportStyles.thValue}>Name</Text>
          </View>
          <View
            style={[
              saleReportStyles.th,
              { width: SUMMARY_COL.balance, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>Balance</Text>
          </View>
          <View style={[saleReportStyles.th, { width: SUMMARY_COL.type }]}>
            <Text style={saleReportStyles.thValue}>Type</Text>
          </View>
        </View>
        {renderSummaryRows(rows)}
        <View style={saleReportStyles.totalsRow}>
          <View
            style={[
              saleReportStyles.th,
              {
                width: SUMMARY_COL.sr + SUMMARY_COL.name,
                textAlign: "right",
              },
            ]}
          >
            <Text style={saleReportStyles.thValue}>Total</Text>
          </View>
          <View
            style={[
              saleReportStyles.th,
              { width: SUMMARY_COL.balance, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>{fmtMoney(total)}</Text>
          </View>
          <View style={[saleReportStyles.th, { width: SUMMARY_COL.type }]}>
            <Text style={saleReportStyles.thValue}>{totalType}</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>List of Accounts</Text>
            {subtitleLines.map((line) => (
              <Text key={line} style={saleReportStyles.reportSubtitle}>
                {line}
              </Text>
            ))}
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={saleReportStyles.tableHead}>
              <View style={[saleReportStyles.th, { width: LIST_COL.sr }]}>
                <Text style={saleReportStyles.thValue}>Sr.#</Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.name }]}>
                <Text style={saleReportStyles.thValue}>Name</Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.mobile }]}>
                <Text style={saleReportStyles.thValue}>Mobile</Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.email }]}>
                <Text style={saleReportStyles.thValue}>Email</Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.acType }]}>
                <Text style={saleReportStyles.thValue}>A/C Type</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: LIST_COL.balance, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Balance</Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.type }]}>
                <Text style={saleReportStyles.thValue}>Type</Text>
              </View>
            </View>

            {renderAccountRows()}

            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width:
                      LIST_COL.sr +
                      LIST_COL.name +
                      LIST_COL.mobile +
                      LIST_COL.email +
                      LIST_COL.acType,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total Debit</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: LIST_COL.balance, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmtMoney(reportData.DebitSum)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.type }]}>
                <Text style={saleReportStyles.thValue}>Debit</Text>
              </View>
            </View>
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width:
                      LIST_COL.sr +
                      LIST_COL.name +
                      LIST_COL.mobile +
                      LIST_COL.email +
                      LIST_COL.acType,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total Credit</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: LIST_COL.balance, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmtMoney(reportData.CreditSum)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: LIST_COL.type }]}>
                <Text style={saleReportStyles.thValue}>Credit</Text>
              </View>
            </View>
          </View>

          <View style={{ marginTop: 10 }}>
            <Text
              style={[saleReportStyles.pagetitle, { fontSize: "10pt", marginBottom: 6 }]}
            >
              Accounts Summary by Type
            </Text>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                width: TABLE_WIDTH,
              }}
            >
              {renderSummaryTable(
                "Credit Types",
                creditAccount,
                reportData.creditTypeTotal,
                "Credit"
              )}
              {renderSummaryTable(
                "Debit Types",
                debitAccount,
                reportData.debitTypeTotal,
                "Debit"
              )}
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
}

export default PrintAccountList;
